/** Confirm + field-selection dialog for restoring an object from a change row.
 *
 * Restores the row's `state_before` values onto the live object via
 * `POST /changes/{id}/restore/`. Only the backend registry's restorable
 * fields are offered; the optional reason travels as `X-Change-Reason` and
 * lands on the resulting `restored` change row.
 */
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import * as changesApi from '@/api/changes';
import type { ObjectChangeDetail } from '@/api/changes';
import { ConsoleApiError } from '@/api/client';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

export function RestoreDialog({
  open,
  change,
  onClose,
  onRestored,
}: {
  open: boolean;
  change: ObjectChangeDetail;
  onClose: () => void;
  onRestored?: (() => void) | undefined;
}) {
  const snackbar = useSnackbar();
  const [selected, setSelected] = useState<string[]>(change.restorable_fields);
  const [reason, setReason] = useState('');
  const [conflict, setConflict] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(change.restorable_fields);
    setReason('');
    setConflict(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, change.id]);

  const mutation = useMutation({
    mutationFn: () => changesApi.restore(change.id, { fields: selected }, reason),
    onSuccess: (r) => {
      snackbar.show(`Restored ${r.restored_fields.length} field(s).`, 'success');
      onClose();
      onRestored?.();
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError && e.status === 409) {
        setConflict(true);
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Restore failed.', 'error');
    },
  });

  const toggle = (field: string) =>
    setSelected((xs) => (xs.includes(field) ? xs.filter((x) => x !== field) : [...xs, field]));

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Restore previous values"
      disableBackdropClose
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!selected.length || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Restoring…' : `Restore ${selected.length} field(s)`}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        The selected fields of <strong>{change.object_repr || change.object_pk}</strong> will be
        set back to their values from before this change. The restore itself is recorded in the
        history.
      </Typography>
      {conflict && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          The object changed since this history was loaded. Review the latest state before
          restoring.
        </Alert>
      )}
      <FormGroup sx={{ mb: 1.5 }}>
        {change.restorable_fields.map((f) => (
          <FormControlLabel
            key={f}
            control={<Checkbox size="small" checked={selected.includes(f)} onChange={() => toggle(f)} />}
            label={
              <span>
                <code>{f}</code>{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  → {formatShort(change.state_before?.[f])}
                </Typography>
              </span>
            }
          />
        ))}
      </FormGroup>
      <TextField
        label="Reason"
        size="small"
        fullWidth
        multiline
        minRows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        helperText="Optional — stored in the change history."
      />
    </AppDialog>
  );
}

function formatShort(value: unknown): string {
  if (value === undefined) return '—';
  const text = value === null ? 'null' : typeof value === 'object' ? JSON.stringify(value) : String(value);
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
}
